import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { capture } from '../lib/analytics';
import { fetchMyPublishedApps, type PublishedAppSummary, unpublishApp } from './publishApi';
import './ManagePublishedPage.css';

/**
 * "Your published apps" — a small dashboard the kid can open to see
 * everything they've shipped. Lists each app's title, version, view
 * count, and last-update time. Per-row actions: open the live page,
 * copy the share link, or take it down (with a typed confirmation
 * because deletes shouldn't be a single tap).
 *
 * This page deliberately stays small. The composer ([PublishView])
 * is where the kid edits an app; this page is where they see the
 * whole portfolio.
 */

type LoadState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; apps: PublishedAppSummary[] };

export function ManagePublishedPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [snippetFlashId, setSnippetFlashId] = useState<string | null>(null);
  const [showSnippetForId, setShowSnippetForId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const apps = await fetchMyPublishedApps();
      if (apps === null) {
        setState({ kind: 'signed-out' });
        return;
      }
      // Most-recent first — kids look for what they just shipped.
      const sorted = [...apps].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      setState({ kind: 'ready', apps: sorted });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not load your apps.';
      setState({ kind: 'error', message: msg });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleUnpublish(appId: string): Promise<void> {
    setBusyId(appId);
    setErrorMessage(null);
    try {
      await unpublishApp(appId);
      setConfirmingId(null);
      await reload();
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Could not unpublish that app.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleCopyLink(url: string): Promise<void> {
    try {
      await navigator.clipboard?.writeText(url);
      capture('share_link_copied', { surface: 'manage-published' });
    } catch {
      // No clipboard API (older browsers / non-secure context) — silent
      // fallback. The kid can still right-click + copy from the link.
    }
  }

  /** Build a paste-ready iframe snippet sized for a typical Notion /
   *  Padlet / classroom-blog embed. The trailing `<script>` listens
   *  for `bryta:resize` postMessage events from the iframe and
   *  resizes it to fit content — so quiz / reactor pages don't end
   *  up with an inner scrollbar inside a Notion embed.
   *
   *  Origin check: the listener compares `event.origin` against the
   *  iframe's src origin so a malicious page on the host can't
   *  spoof a resize on a legitimate Bryta embed. */
  function buildEmbedSnippet(url: string, frameId: string): string {
    // Append `?embed=1` so the runtime can hide chrome that's redundant
    // in an embed (footer, QR, share cluster). `?embed=1` is also the
    // signal the page uses to decide when to broadcast height — works
    // even when the cross-origin iframe-detection path is shaky.
    const sep = url.includes('?') ? '&' : '?';
    const embedUrl = `${url}${sep}embed=1`;
    return (
      `<iframe src="${embedUrl}" id="${frameId}" width="100%" height="520" ` +
      `loading="lazy" ` +
      `allow="camera; microphone; autoplay; clipboard-write" ` +
      `style="border: 1px solid #ddd; border-radius: 12px; max-width: 720px;"></iframe>\n` +
      `<script>(function(){` +
      `var f=document.getElementById('${frameId}');` +
      `if(!f)return;` +
      `var origin=new URL(f.src).origin;` +
      `window.addEventListener('message',function(e){` +
      `if(e.origin!==origin||!e.data||e.data.type!=='bryta:resize')return;` +
      `var h=Math.max(120,Math.min(4000,Number(e.data.height)||0));` +
      `if(h)f.style.height=h+'px';` +
      `});` +
      `})();</script>`
    );
  }

  async function handleCopySnippet(url: string, appId: string): Promise<void> {
    const snippet = buildEmbedSnippet(url, `bryta-app-${appId}`);
    try {
      await navigator.clipboard?.writeText(snippet);
      setSnippetFlashId(appId);
      window.setTimeout(
        () => setSnippetFlashId((current) => (current === appId ? null : current)),
        1600,
      );
    } catch {
      setErrorMessage('Could not copy the snippet — try again or paste it manually.');
    }
  }

  return (
    <main className="bl-manage-published">
      <header className="bl-manage-published__header">
        <Link to="/studio" className="bl-manage-published__back">
          ← Back to studio
        </Link>
        <h1 className="bl-manage-published__title">Your published apps</h1>
        <p className="bl-manage-published__hint">
          Everything you&apos;ve shipped lives here. Open one to share it, or take it down if you
          want to keep working on it.
        </p>
      </header>

      {state.kind === 'loading' && (
        <p className="bl-manage-published__notice">Loading your apps…</p>
      )}

      {state.kind === 'error' && (
        <p className="bl-manage-published__notice bl-manage-published__notice--error">
          {state.message}
        </p>
      )}

      {state.kind === 'signed-out' && (
        <p className="bl-manage-published__notice">Sign in to see your published apps.</p>
      )}

      {state.kind === 'ready' && state.apps.length === 0 && (
        <p className="bl-manage-published__notice">
          You haven&apos;t published anything yet. Train a model in the studio, then click{' '}
          <strong>Publish to bryta.app</strong> when you&apos;re ready.
        </p>
      )}

      {state.kind === 'ready' && state.apps.length > 0 && (
        <ul className="bl-manage-published__list">
          {state.apps.map((app) => {
            const updated = new Date(app.updatedAt);
            const updatedLabel = isNaN(updated.getTime())
              ? app.updatedAt
              : updated.toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                });
            const isConfirming = confirmingId === app.id;
            const isBusy = busyId === app.id;
            return (
              <li key={app.id} className="bl-manage-published__row" aria-busy={isBusy}>
                <div className="bl-manage-published__row-head">
                  <p className="bl-manage-published__row-title">{app.title}</p>
                  <p className="bl-manage-published__row-meta">
                    <span>v{app.version}</span>
                    {typeof app.viewCount === 'number' && app.viewCount > 0 && (
                      <span aria-label={`${app.viewCount} visits`}>
                        <span aria-hidden="true">👀 </span>
                        {app.viewCount === 1 ? '1 visit' : `${app.viewCount} visits`}
                      </span>
                    )}
                    <span className="bl-manage-published__row-updated">Updated {updatedLabel}</span>
                  </p>
                </div>
                <a
                  className="bl-manage-published__row-url"
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {app.url}
                </a>
                <div className="bl-manage-published__row-actions">
                  <a
                    className="bl-manage-published__btn"
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open live ↗
                  </a>
                  <button
                    type="button"
                    className="bl-manage-published__btn"
                    onClick={() => void handleCopyLink(app.url)}
                  >
                    Copy link
                  </button>
                  <button
                    type="button"
                    className="bl-manage-published__btn"
                    aria-expanded={showSnippetForId === app.id}
                    onClick={() => setShowSnippetForId((id) => (id === app.id ? null : app.id))}
                  >
                    {showSnippetForId === app.id ? 'Hide embed' : 'Embed'}
                  </button>
                  {!isConfirming && (
                    <button
                      type="button"
                      className="bl-manage-published__btn bl-manage-published__btn--danger"
                      onClick={() => setConfirmingId(app.id)}
                      disabled={isBusy}
                    >
                      Unpublish
                    </button>
                  )}
                  {isConfirming && (
                    <span className="bl-manage-published__confirm">
                      <span>Take it down?</span>
                      <button
                        type="button"
                        className="bl-manage-published__btn bl-manage-published__btn--danger"
                        onClick={() => void handleUnpublish(app.id)}
                        disabled={isBusy}
                      >
                        {isBusy ? 'Removing…' : 'Yes, unpublish'}
                      </button>
                      <button
                        type="button"
                        className="bl-manage-published__btn"
                        onClick={() => setConfirmingId(null)}
                        disabled={isBusy}
                      >
                        Keep it
                      </button>
                    </span>
                  )}
                </div>
                {showSnippetForId === app.id && (
                  <div className="bl-manage-published__embed" aria-label="Embed snippet">
                    <p className="bl-manage-published__embed-hint">
                      Paste this anywhere — Notion, Padlet, a classroom blog. The iframe asks for
                      camera + mic permission inside the host page.
                    </p>
                    <textarea
                      className="bl-manage-published__embed-code"
                      readOnly
                      rows={3}
                      value={buildEmbedSnippet(app.url, `bryta-app-${app.id}`)}
                      onClick={(e) => e.currentTarget.select()}
                      aria-label={`Embed snippet for ${app.title}`}
                    />
                    <button
                      type="button"
                      className="bl-manage-published__btn"
                      onClick={() => void handleCopySnippet(app.url, app.id)}
                    >
                      {snippetFlashId === app.id ? 'Copied!' : 'Copy snippet'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {errorMessage && (
        <p className="bl-manage-published__notice bl-manage-published__notice--error" role="alert">
          {errorMessage}
        </p>
      )}
    </main>
  );
}
